import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import type { VMSCamera } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Filter option constants
// ---------------------------------------------------------------------------

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'dismissed', label: 'Dismissed' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IncidentsControlBarProps {
  /** Current severity filter value (empty string = all) */
  severity: string;
  onSeverityChange: (value: string) => void;

  /** Current status filter value (empty string = all) */
  status: string;
  onStatusChange: (value: string) => void;

  /** Currently selected camera (null = all cameras) */
  selectedCamera: VMSCamera | null;
  onCameraChange: (camera: VMSCamera | null) => void;

  /** Available cameras for the Autocomplete dropdown */
  cameras: VMSCamera[];

  // Search removed during parking — implement with backend support when resuming

  /** Sound notification toggle */
  soundEnabled: boolean;
  onToggleSound: () => void;

  /** Refresh handler */
  onRefresh: () => void;

  /** Create incident handler */
  onCreateIncident: () => void;

  /** Open Checklist Mapping Settings drawer */
  onChecklistMapping?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Control bar for the Incidents tab.
 *
 * Layout (left-to-right):
 *   "+ New Incident" button | Severity Select | Status Select | Camera Autocomplete
 *   ...flex spacer...
 *   Sound toggle | Refresh | Search TextField
 */
export function IncidentsControlBar({
  severity,
  onSeverityChange,
  status,
  onStatusChange,
  selectedCamera,
  onCameraChange,
  cameras,
  soundEnabled,
  onToggleSound,
  onRefresh,
  onCreateIncident,
  onChecklistMapping,
}: IncidentsControlBarProps) {
  const handleSeverityChange = (e: SelectChangeEvent<string>) => {
    onSeverityChange(e.target.value);
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    onStatusChange(e.target.value);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {/* Left group: action button + filters */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onCreateIncident}
        size="small"
      >
        New Incident
      </Button>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Severity</InputLabel>
        <Select
          value={severity}
          label="Severity"
          onChange={handleSeverityChange}
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={status}
          label="Status"
          onChange={handleStatusChange}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
        size="small"
        sx={{ minWidth: 200 }}
        options={cameras}
        value={selectedCamera}
        onChange={(_event, newValue) => onCameraChange(newValue)}
        getOptionLabel={(option) =>
          option.display_name || option.name || `Camera ${option.id}`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} label="Camera" placeholder="All Cameras" />
        )}
      />

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Right group: icon buttons + search */}
      <Tooltip title={soundEnabled ? 'Mute incident sounds' : 'Enable incident sounds'}>
        <IconButton onClick={onToggleSound} size="small">
          {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Checklist Mapping Settings">
        <IconButton size="small" onClick={onChecklistMapping}>
          <AssignmentIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Refresh incidents">
        <IconButton onClick={onRefresh} size="small">
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      {/* Search removed during parking — implement with backend support when resuming */}
    </Paper>
  );
}

export default IncidentsControlBar;
